// client/src/hooks/useEventsPagination.js
import { useState, useCallback, useMemo } from 'react'

/**
 * Custom hook to manage infinite scroll pagination for events
 *
 * Features:
 * - Tracks pagination offset
 * - Manages "has more" state
 * - Manages loading state for pagination
 * - Provides clean reset functionality
 *
 * @returns {Object} Pagination state and handlers
 */
export default function useEventsPagination() {
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  /**
   * Load more items using the provided fetch function
   * @param {Function} fetchFn - Async function that fetches more items
   *                             Should return { newEvents, hasMore }
   */
  const loadMore = useCallback(
    async (fetchFn) => {
      if (loadingMore || !hasMore) {
        return
      }

      try {
        setLoadingMore(true)
        const newOffset = offset + 100

        // Call the provided fetch function with the new offset
        const result = await fetchFn(newOffset)

        setOffset(newOffset)

        // Update hasMore based on result if provided
        if (result && typeof result.hasMore === 'boolean') {
          setHasMore(result.hasMore)
        } else if (result && result.newEvents) {
          // Infer hasMore from newEvents length
          setHasMore(result.newEvents.length === 100)
        }
      } catch (err) {
        console.error('Pagination error:', err)
      } finally {
        setLoadingMore(false)
      }
    },
    [offset, loadingMore, hasMore],
  )

  /**
   * Reset pagination to initial state
   */
  const reset = useCallback(() => {
    setOffset(0)
    setHasMore(true)
    setLoadingMore(false)
  }, [])

  return useMemo(
    () => ({
      // State
      offset,
      hasMore,
      loadingMore,

      // Methods
      loadMore,
      reset,
      setHasMore,
    }),
    // Only include state values, not callbacks (already stable via useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offset, hasMore, loadingMore],
  )
}
